// controllers/Fields.jsx (or wherever you keep them)
import { useField } from "formik";
import moment from "moment";
import styles from "./quotationControllers.module.css";
import { FormTextArea, TextInput } from "../../../components/input/inputs";
// Assuming these exist in your project:
// import { Label, Asterisk, Tooltip, ErrorLabel, DropDown } from "../../../components/...";
import {FormFieldLabel as Label, FormErrorLabel as ErrorLabel} from "../../../components/label/Labels";
import { formatDateInDMY, formatISOToDDMonYYYY, getLabelFromValue } from "../../../utils/arrayUtils";
import Asterisk from "../../../components/asterisk/Asterisk";
import DropDown from "../../../components/dropDown/DropDown";
import { useState } from "react";
import { Popover, ArrowContainer } from 'react-tiny-popover'
import IMAGES from "../../../assets/images/images";
import * as Yup from "yup";
import DatePickerField from "../../../components/datePicker/DatePickerField";
import { utcUnixSecondsToDate } from "../../../utils/dateTime";
// And helper(s):
// import { getLabelFromValue, SelectFieldObj } from "../../../../components/...";

const Tooltip = ({ text }) => {
    let [isPopoverOpen, setIsPopoverOpen] = useState(false);
    return (
        <Popover
            isOpen={isPopoverOpen}
            positions={['top']} // preferred positions by priority
            containerClassName={styles.popoverContainer}
            content={({ position, childRect, popoverRect }) => (
                <ArrowContainer // if you'd like an arrow, you can import the ArrowContainer!
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={'#f5f3f3'}
                    arrowSize={10}
                    className='popover-arrow-container'
                    arrowClassName='popover-arrow'
                >
                    <div
                        className={styles.tooltipBody}
                    >
                        {text}
                    </div>
                </ArrowContainer>
            )}


        >
            <img src={IMAGES.infoCircle}
                onMouseLeave={() => setIsPopoverOpen((isPopoverOpen = false))}
                onMouseOver={() => setIsPopoverOpen((isPopoverOpen = true))}
            />

        </Popover>
    );
}

// Text input controller
export const TextField = ({ disabled = false, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const hasError = meta.touched && meta.error ? true : false;
  const inputStyle = hasError ? `${styles.input} ${styles.inputError}` : styles.input;


  const handleChange = (e) => {
    field.onChange(e);
    if (props.onChange) {
      props.onChange(e.target.value, { field, meta, helpers });
    }
  };


  return (
    <div className={props.isExpandedField ? `${styles.field} ${styles.expandedField}` : styles.field}>
      <div>
        {props.label&&<Label text={props.label} style={styles.lable} />}
        {props.isMandatory && props.label ? <Asterisk /> : null}
        {props.showTooltip ? (
          <>
            &nbsp;&nbsp;<Tooltip text={props.tooltipText} />
          </>
        ) : null}
      </div>

      {!props.isPreviewMode ? (
        <>
          <TextInput
            name={props.name}
            placeholder={props.placeholder}
            type={props.type}
            // disabled={disabled}
             disabled={disabled}
            value={field.value ?? ""}
            {...field}
            onChange={handleChange} 
            style={inputStyle}
            inputRight={props.inputRight}
          />
          {props.textLimit ? <span className={styles.textLimitInfo}>{props.textLimit}</span> : null}
          {hasError ? <ErrorLabel text={meta.error} /> : null}
        </>
      ) : (
        <div className={styles.previewText}>{field?.value ? field?.value : "---"}</div>
      )}
    </div>
  );
};
let SelectFieldObj = {
    width: "auto"
}
// Select controller
export const SelectField = ({ options, serviceConfiguration=null, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const hasError = meta.touched && meta.error ? true : false;
  const value =
    options?.length > 0 && field.value
      ? options.find((opt) => opt.value === field.value) ?? ""
      : "";

  return (
    <div className={props.isExpandedField ? `${styles.field} ${styles.expandedField}` : styles.field}>
      <div>
        {props.label&&<Label text={props.label} style={styles.lable} />}
        {props.isMandatory ? <Asterisk /> : null}
        {props.showTooltip ? (
          <>
            &nbsp;&nbsp;<Tooltip text={props.tooltipText} />
          </>
        ) : null}
      </div>

      {!props.isPreviewMode || serviceConfiguration ? (
        <>
          <DropDown
            options={options}
            onChange={({ label, value }) => {
              if (props.onChange) props.onChange(value, { field, meta, helpers });
              helpers.setValue(value);
            }}
            value={value}
            hasError={hasError}
            // isDisabled={props.isDisabled}
            isDisabled={props.isDisabled}
            customStyles={SelectFieldObj}
            placeholder={props.placeholder}
            menuPortalTarget={null}
            menuPosition="absolute"
            menuShouldBlockScroll={false}
          />
          {hasError ? <ErrorLabel text={meta.error} /> : null}
        </>
      ) : (
        <div className={styles.previewText}>
          {field?.value ? getLabelFromValue(options, field?.value) : "---"}
        </div>
      )}
    </div>
  );
};

// Date/time controller
export const DateTimeTextField = ({ disabled = false, ...props }) => {
  const [field, meta, helpers] = useField(props);
  const hasError = meta.touched && meta.error ? true : false;
  const inputStyle = hasError ? `${styles.input} ${styles.inputError}` : styles.input;

  return (
    <div className={props.isExpandedField ? `${styles.field} ${styles.expandedField}` : styles.field}>
      <div>
        {props.label&&<Label text={props.label} style={styles.lable} />}
        {props.isMandatory ? <Asterisk /> : null}
        {props.showTooltip ? (
          <>
            &nbsp;&nbsp;<Tooltip text={props.tooltipText} />
          </>
        ) : null}
      </div>

      {!props.isPreviewMode ? (
        <>
          <TextInput
            name={props.name}
            placeholder={props.placeholder}
            type={props.type}          // can be "date" or "datetime-local"
            disabled={!props.isPreviewMode?disabled:props.isPreviewMode}
            max={props.max}
            value={field.value ?? ""}
            onChange={(e) => helpers.setValue(e.target.value)}
            onBlur={() => helpers.setTouched(true)}
            style={inputStyle}
          />
          {props.textLimit ? <span className={styles.textLimitInfo}>{props.textLimit}</span> : null}
          {hasError ? <ErrorLabel text={meta.error} /> : null}
        </>
      ) : (
        <div className={styles.previewText}>
          {props.type === "datetime-local" && field?.value
            ? moment(field?.value).format("DD MMM YYYY HH:mm")
            : field?.value || "---"}
        </div>
      )}
    </div>
  );
};

export const TextArea = ({ ...props }) => {
    const [field, meta, helpers] = useField(props);
    const hasError = meta.touched && meta.error ? true : false;
    const inputStyle = hasError
        ? `${styles.input} ${styles.inputError}`
        : styles.input;
    return (
        <div className={props.isExpandedField ? `${styles.Field} ${styles.expandedField}` : `${styles.Field}`}>
            {props.label &&<div>
                <Label
                    htmlFor={props.htmlFor}
                    text={props.label}
                    style={styles.lable}
                />
                {props.isMandatory ? <Asterisk /> : <></>}
                {
                    props.showTooltip ?
                        <>
                            &nbsp;&nbsp;
                            <Tooltip text={props.tooltipText} />
                        </>
                        : <></>
                }
            </div>}
            {
                 !props.isPreviewMode?
                 <>
                   <FormTextArea
                    {...field} 
                    placeholder={props.placeholder} 
                    style={inputStyle} />
                    {props.textLimit ? <span className={styles.textLimitInfo}>{props.textLimit}</span> : null}
                    {hasError ? <ErrorLabel text={meta.error} /> : null}
                 </>:
                //<div className={styles.previewText}>{field?.value?field?.value:"---"}</div>
                <div className={styles.previewText}>
                   {field?.value?field?.value?.split('\n')?.map(text => <div>{text}</div>):"---"}
                </div>
            }
          
        </div>
    );
};


export const AgreementStatusControl = ({
  nameStatus = "agreementStatus.status",
  nameDate = "agreementStatus.signedDate",
  label = "Agreement Status",
  isMandatory = false,
  isPreviewMode = false,
  disabled = false,
  showTooltip = false,
  tooltipText = "",
  // Pass-through constraints to the date field (YYYY-MM-DD)
  min,
  max,
  dateLabel = "Signed Date",
}) => {
  const [statusField, statusMeta, statusHelpers] = useField(nameStatus);
  const [dateField, , dateHelpers] = useField(nameDate);

  const isSigned = statusField.value === "signed";
  const isDisabled = isPreviewMode || disabled;

  const statusError = statusMeta.touched && statusMeta.error ? statusMeta.error : null;
  const statusErrorId = `${nameStatus}-error`;

  const handleSelectSigned = () => {
    if (isDisabled) return;
    statusHelpers.setValue("signed");
    statusHelpers.setTouched(true, false);
    dateHelpers.setTouched(false, false);
  };

  const handleSelectToBeSigned = () => {
    if (isDisabled) return;
    statusHelpers.setValue("to_be_signed");
    statusHelpers.setTouched(false, false);
    dateHelpers.setTouched(false, false);
    // clear hidden date so it won't leak in payload
    dateHelpers.setValue(null);
  };

  // Preview mode: simple, read-only text
  if (isPreviewMode) {
    return (
      <div className={styles.field}>
        <div>
          {label && <Label text={label} style={styles.label} />}
          {isMandatory ? <Asterisk /> : null}
          {showTooltip ? (
            <>
              &nbsp;&nbsp;<Tooltip text={tooltipText} />
            </>
          ) : null}
        </div>

        <div className={styles.previewText}>
          {isSigned
            ? (dateField.value ? `Signed On ${formatDateInDMY(dateField.value)}` : "Signed")
            : "To Be Signed"}
        </div>
      </div>
    );
  }

  // Edit mode
  return (
    <div className={styles.field}>
      <div>
        {label && <Label text={label} style={styles.label} />}
        {isMandatory ? <Asterisk /> : null}
        {showTooltip ? (
          <>
            &nbsp;&nbsp;<Tooltip text={tooltipText} />
          </>
        ) : null}
      </div>

      <div className={styles.agreementRow}>
        {/* Signed */}
        <label className={styles.radio}>
          <input
            type="radio"
            name={nameStatus}
            value="signed"
            checked={isSigned}
            onChange={handleSelectSigned}
            onBlur={() => statusHelpers.setTouched(true, true)}
            disabled={isDisabled}
            aria-invalid={!!statusError}
            aria-describedby={statusError ? statusErrorId : undefined}
          />
          <span className={styles.agreementlabel}>Signed</span>
        </label>

        {/* Inline Date using the shared controller */}
        <div id="datepicker" className={styles.inlineDateWrapper}>
        {/* {isSigned && ( */}
            {/* <DateTimeTextField
              name={nameDate}
              type="date"
              label={null}
              // isMandatory
              isPreviewMode={false} 
              disabled={isDisabled || !isSigned}
              min={min}
              max={max}
              // Optional: tap into changes if you need side effects
              onChange={(e) => {
                // This keeps DateTimeTextField as source of truth,
                // and also gives you a hook for custom logic if needed
                dateHelpers.setValue(e.target.value);
              }}
            /> */}
              <DatePickerField
                name={nameDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/MM/yyyy"
                isPreviewMode={false}
                disabled={isDisabled || !isSigned}
                labelstyle={styles.labelstyle}
              />
          {/* )} */}
          </div>

        {/* To Be Signed */}
        <label className={styles.radio} style={{ marginLeft: 12 }}>
          <input
            type="radio"
            name={nameStatus}
            value="to_be_signed"
            checked={statusField.value === "to_be_signed"}
            onChange={handleSelectToBeSigned}
            onBlur={() => statusHelpers.setTouched(true, true)}
            disabled={isDisabled}
            aria-invalid={!!statusError}
            aria-describedby={statusError ? statusErrorId : undefined}
          />
          <span className={styles.agreementlabel}>To Be Signed</span>
        </label>
      </div>

      {/* Status error (date errors are shown by DateTimeTextField itself) */}
      {statusError ? (
        <div className={styles.error}>
          <ErrorLabel id={statusErrorId} text={statusError} />
        </div>
      ) : null}
    </div>
  );
};

export const notInPast = Yup.string().test(
  "not-in-past",
  "Date cannot be in the past",
  function (value) {
    if (!value) return true; // empty handled by required()
    const selected = utcUnixSecondsToDate(value);
    selected.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return selected >= today;
  }
);
export const mobileNumberValidator = Yup.string()
  .matches(/^\+?\d+(-\d+)*$/, "Invalid number format") //  /^\+?\d+$/
  .max(20, "Number cannot exceed 20 digits");

